import React, { createContext, useContext, useEffect, useReducer, useCallback, useMemo } from "react";
import { createEcho } from "../api/notifications";
import { notificationsAPI } from "../features/notifications/notifications.api";
import { mapNotification, type AppNotification } from "../features/notifications/types";

// ── State ─────────────────────────────────────────────────────────────────────

interface State {
  items: AppNotification[];
  loading: boolean;
  toast: AppNotification | null;   // latest real-time notification for toast
}

type Action =
  | { type: "SET"; payload: AppNotification[] }
  | { type: "ADD"; payload: AppNotification }
  | { type: "MARK_READ"; id: string }
  | { type: "MARK_ALL_READ" }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "CLEAR_TOAST" };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET":         return { ...state, items: action.payload, loading: false };
    case "ADD":         return { ...state, items: [action.payload, ...state.items], toast: action.payload };
    case "MARK_READ":   return { ...state, items: state.items.map((n) => n.id === action.id ? { ...n, isRead: true } : n) };
    case "MARK_ALL_READ": return { ...state, items: state.items.map((n) => ({ ...n, isRead: true })) };
    case "SET_LOADING": return { ...state, loading: action.payload };
    case "CLEAR_TOAST": return { ...state, toast: null };
    default:            return state;
  }
};

// ── Context ───────────────────────────────────────────────────────────────────

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  toast: AppNotification | null;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  clearToast: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  userId?: string | number | null;
}> = ({ children, userId }) => {
  const [state, dispatch] = useReducer(reducer, { items: [], loading: false, toast: null });

  // Fetch on mount / user change
  useEffect(() => {
    if (!userId) return;
    dispatch({ type: "SET_LOADING", payload: true });
    notificationsAPI.getAll()
      .then((items) => dispatch({ type: "SET", payload: items }))
      .catch(() => dispatch({ type: "SET_LOADING", payload: false }));
  }, [userId]);

  // Real-time via Laravel Echo
  useEffect(() => {
    if (!userId) return;
    const echo = createEcho();
    if (!echo) return;

    const channel = echo.private(`user.${userId}`);
    channel.listen(".notification.created", (n: any) => {
      dispatch({ type: "ADD", payload: mapNotification(n) });
    });

    return () => { echo.leave(`user.${userId}`); echo.disconnect(); };
  }, [userId]);

  const markRead = useCallback(async (id: string) => {
    dispatch({ type: "MARK_READ", id });
    await notificationsAPI.markRead(id).catch(() => {});
  }, []);

  const markAllRead = useCallback(async () => {
    dispatch({ type: "MARK_ALL_READ" });
    await notificationsAPI.markAllRead().catch(() => {});
  }, []);

  const clearToast = useCallback(() => dispatch({ type: "CLEAR_TOAST" }), []);

  const unreadCount = useMemo(() => state.items.filter((n) => !n.isRead).length, [state.items]);

  return (
    <NotificationContext.Provider value={{
      notifications: state.items,
      unreadCount,
      loading: state.loading,
      toast: state.toast,
      markRead, markAllRead, clearToast,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
};
